'use strict';
import sxQuery from '../../sxQuery/sxQuery';
import StringHelper from '../../global/StringHelper';
import Helper from '../utils/Helper';
import Logger from '../../global/Logger';
import UiHelper from '../ui/UiHelper';
import Icons from './Icons';
import SxQueryObject from '../../sxQuery/SxQueryObject';
import FilterPosition from '../enums/FilterPosition';
import FilterGroupBuilder from './FilterGroupBuilder';
import FilterType from '../enums/FilterType';
import DatePrecision from '../enums/DatePrecision';
import SxQueryUtils from '../../sxQuery/SxQueryUtils';
import MediaQueryMatchType from '../../sxQuery/MediaQueryMatchType';
import UiColors from '../styles/UiColors';
import NavigationPosition from '../enums/NavigationPosition';
import { NavigationType } from '../interface/LayoutConfiguration';
const persistentFilterOptions = {
    ss360AreMobileSuggestsOpen: false
};
const FILTER_GROUP_SEPARATOR = '___';
class Filter {
    constructor(context, filterGroups, activeOptions, query, onChange, filterIdToName, totalResults, filterMapping, fallbackNames = {}, optionViewNames = {}, activeGuidedQuestions = [], position, forceSlideIn = false, deleteFilterBottom = false, toggleButtonLabel, filterModificationMap = {}, filterDeletionValueMap = {}, allFilters = [], filterClusters = [], forceResetExpandedGroups = false) {
        this.filterClusters = [];
        this.context = context;
        this.filterConfig = context.pluginConfiguration.filters;
        this.filterGroups = filterGroups;
        this.position = position !== undefined ? position : this.filterConfig.position;
        this.filterLayerId = this.position === FilterPosition.Left ? context.filterLayerId : context.filterTopLayerId;
        this.query = query;
        this.onChange = onChange;
        this.totalResults = totalResults;
        this.forceSlideIn = forceSlideIn;
        this.deleteFilterBottom = deleteFilterBottom;
        this.toggleButtonLabel = toggleButtonLabel;
        this.allFilters = allFilters;
        this.filterClusters = filterClusters;
        this.filterModificationMap = filterModificationMap;
        this.filters = [];
        this.units = {};
        this.hasOptions = false;
        this.filterDeletionValueMap = filterDeletionValueMap;
        this.isSimpleValueFormat = true;
        this.groupCache = context.getFilterViewGroupCache(this.position === FilterPosition.Top);
        const groupCache = this.groupCache;
        this.previousFilterGroups = Object.keys(groupCache.groupByKey);
        this.fallbackNames = fallbackNames;
        this.optionViewNames = optionViewNames;
        this.activeGuidedQuestions = activeGuidedQuestions || [];
        this.appendStyles();
        this.ensureFilterLayer();
        this.createKeyToNameMapping(filterIdToName);
        const activeElement = this.filterLayer.getActiveElement();
        const onExpand = () => {
            this.expandHandler();
        };
        const onChangeHandler = (key, value, label) => {
            this.changeHandler(key || label, value);
        };
        this.builder = new FilterGroupBuilder(context, onExpand, onChangeHandler, persistentFilterOptions, groupCache, this.position === FilterPosition.Top, this.position);
        Object.values(groupCache.viewGroupByKey).forEach((viewGroup) => {
            viewGroup.updateHandlers(onChangeHandler, onExpand);
        });
        // array to map
        this.activeFilter = (activeOptions || []).reduce((acc, obj) => {
            const key = Object.keys(obj)[0];
            acc[key] = obj[key];
            return acc;
        }, {});
        this.activeOptions = [...activeOptions];
        this.allFilters.forEach((f) => {
            if (f.type === FilterType.CategoryTree || f.filterType === FilterType.CategoryTree || f.type === FilterType.Tree || f.filterType === FilterType.Tree) {
                f.categories = this.parseCategoryTreeValues(f.key, f.categories || []);
            }
        });
        const isClustered = (this.filterClusters || []).length > 0;
        const keyFilterMap = isClustered ? this.filterGroups.reduce((acc, filterGroup) => {
            acc[filterGroup.key] = filterGroup;
            return acc;
        }, {}) : {};
        const clusteredFilters = !isClustered ? [
            {
                filters: this.filterGroups
            }
        ] : this.filterClusters.sort((a, b) => {
            if (a.name === 'default')
                return -1;
            if (b.name === 'default')
                return 1;
            return 0;
        }).filter(c => c.filterOptions.length > 0).map((cluster) => ({
            label: cluster.name !== 'default' ? cluster.name : undefined,
            filters: cluster.filterOptions.map((key) => keyFilterMap[key]).filter((filterGroup) => filterGroup !== undefined)
        }));
        if (!forceResetExpandedGroups) {
            this.initExpandedGroups();
        }
        else {
            this.resetExpandedGroups();
        }
        this.renderFilters(clusteredFilters);
        this.previousFilterGroups.forEach((groupKey) => {
            if (groupCache.groupByKey[groupKey] !== undefined) {
                groupCache.groupByKey[groupKey].wrapper.remove();
            }
            delete groupCache.groupByKey[groupKey];
            delete groupCache.viewGroupByKey[groupKey];
        });
        const positionIncrement = this.filterLayer.findChildren('*:not(.ss360-filter__group):not(.ss360-filter__group-label)').length;
        let clusterPositionIncrement = 0;
        let positionDecrement = 0;
        clusteredFilters.forEach((cluster) => {
            const filterGroups = cluster.filters;
            const max = Math.min((this.filterConfig.limit || 9999), filterGroups.length);
            for (let i = 0; i < max; i++) {
                const key = cluster.label ? `${StringHelper.getSafeKey(cluster.label)}${FILTER_GROUP_SEPARATOR}${filterGroups[i].key}` : filterGroups[i].key;
                const filterViewGroup = this.groupCache.viewGroupByKey[key];
                if (filterViewGroup === undefined) {
                    positionDecrement++;
                    continue;
                }
                if (filterViewGroup.getWrapper().isMounted()) {
                    this.filterLayer.ensureChildPosition(filterViewGroup.getWrapper().get(0), i + positionIncrement - positionDecrement + clusterPositionIncrement);
                }
            }
            clusterPositionIncrement += max;
        });
        this.renderMobileToggle();
        this.mount();
        this.handleFilterGroups(clusteredFilters);
        this.renderDeleteBar();
        groupCache.filters = this.filters;
        this.bindMappings(filterMapping);
        const filterRenderedCallback = context.pluginConfiguration.callbacks.filterRendered;
        if (filterRenderedCallback !== undefined && (typeof filterRenderedCallback) === 'function') {
            filterRenderedCallback();
        }
        setTimeout(() => {
            this.filterLayer.find('.ss360-filter__group').addClass('ss360-filter__group--mounted');
        }, 0);
        if (groupCache.lastInteractedGroupKey !== undefined && groupCache.lastInteractedGroupOffsetTop !== undefined) {
            const isSlideIn = this.isSlideIn();
            sxQuery(isSlideIn ? this.filterLayer : window).off('scroll.filterJump');
            const $node = sxQuery(`#ss360-filter-${StringHelper.getSafeKey(groupCache.lastInteractedGroupKey)} .ss360-filter__button`);
            const offsetTop = isSlideIn ? this.filterLayer.offset().top : sxQuery(window).scrollTop();
            const scrollTop = $node.offset().top - offsetTop;
            const diff = scrollTop - groupCache.lastInteractedGroupOffsetTop;
            if (isSlideIn) {
                this.filterLayer.scrollTopBy(diff);
            }
            else if (this.position !== FilterPosition.Top) {
                sxQuery(window).scrollTopBy(diff);
            }
        }
        if (this.isSlideIn() && this.context.pluginConfiguration.layout.navigation.position !== NavigationPosition.Left) {
            this.context.layer.get().collapseSidebar();
        }
        if (this.forceSlideIn) { // ensure mobile toggle is always the last child
            const mobileToggle = this.context.layer.get().getMobileFilterToggle();
            if (mobileToggle && mobileToggle.length > 0 && mobileToggle.get(0).nextElementSibling !== null) {
                mobileToggle.parent().append(mobileToggle);
            }
        }
        if (this.isSlideIn() || this.position === FilterPosition.Left) {
            let $applySection = this.filterLayer.find('.ss360-filter__apply');
            if ($applySection.length === 0) {
                $applySection = sxQuery('<div class="ss360-filter__apply"></div>');
                const $applyButton = sxQuery('<button type="button" class="ss360-n-button ss360-filter__apply-button"></button>');
                $applyButton.text(this.filterConfig.applyAndCloseLabel);
                $applySection.append($applyButton);
            }
            $applySection.find('.ss360-filter__apply-button').off('click.apply').on('click.apply', () => {
                this.hideMobile();
            });
            this.filterLayer.append($applySection);
        }
        if (activeElement && !SxQueryUtils.isElementActive(activeElement) && sxQuery(activeElement).parents(`#${this.filterLayerId}`, true).length > 0) {
            setTimeout(() => {
                sxQuery(activeElement).focus();
            }, 0);
        }
    }
    isSlideIn() {
        return SxQueryUtils.matchesMediaQuery(MediaQueryMatchType.Max, 991) || this.forceSlideIn;
    }
    appendStyles() {
        const { style } = this.context.pluginConfiguration;
        if (style.defaultCss) {
            const $target = style.targetNode ? sxQuery(style.targetNode) : sxQuery(document);
            const $appendNode = $target.find('head').length > 0 ? $target.find('head') : $target;
            $appendNode.find('#ss360-filter-css').remove();
            // eslint-disable-next-line
            const css = require('../../../sass/siteSearch/filters.scss');
            const additionalCss = `.ss360-multiselect__search {background-image: url(${Icons.getBase64EncodedIcon(Icons.MAGNIFIER, UiColors.DIM_GREY)})}`;
            $appendNode.append(`<style id="ss360-filter-css" type="text/css">${css}${additionalCss}</style>`);
        }
    }
    initExpandedGroups() {
        if (!('expandedFilters' in persistentFilterOptions) && this.position === FilterPosition.Left) {
            const filterConfig = this.filterConfig;
            persistentFilterOptions.expandedFilters = [];
            if (filterConfig.expandedGroupsCount !== undefined && filterConfig.expandedGroupsCount !== -1 && filterConfig.expandedGroupsCount !== -1) {
                // @ts-ignore
                const expandedGroupsCount = parseInt(filterConfig.expandedGroupsCount, 10);
                for (let i = 0; i < this.filterGroups.length; i++) {
                    const group = this.filterGroups[i];
                    if (this.activeFilter[group.key] !== undefined || i < expandedGroupsCount) {
                        persistentFilterOptions.expandedFilters.push(group.key);
                    }
                }
            }
        }
    }
    resetExpandedGroups() {
        delete persistentFilterOptions.expandedFilters;
        this.initExpandedGroups();
    }
    ensureFilterLayer() {
        const groupCache = this.groupCache;
        let filterLayer = groupCache.layer;
        if (filterLayer !== undefined && !filterLayer.isMounted()) { // new instance, js navigation
            filterLayer = undefined;
            groupCache.groupByKey = {};
            groupCache.activeGroups = [];
            groupCache.layer = undefined;
            groupCache.searchQueries = {};
            groupCache.expandedGroups = {};
        }
        const filterConfig = this.filterConfig;
        const layer = this.context.layer;
        const isTop = this.position === FilterPosition.Top;
        if (filterLayer === undefined) {
            const frame = layer.get();
            filterLayer = frame.getFilterLayer(this.position);
            frame.detachFilterFrame(isTop);
        }
        filterLayer.addClass('ss360-filter--mounting');
        groupCache.layer = filterLayer;
        this.filterLayer = filterLayer;
        if (this.position === FilterPosition.Left) {
            const hl = filterConfig.headingLevel;
            const frame = layer.get();
            if (filterLayer.find('.ss360-filter__header').length === 0) {
                const $heading = sxQuery(`<h${hl} class="ss360-filter__heading"></h${hl}>`);
                ;
                $heading.html(filterConfig.label);
                const $closeButton = UiHelper.createCloseLayerButton('filter', 'Close Filter');
                const $header = sxQuery('<div class="ss360-filter__header"></div>');
                $header.append($heading);
                $header.append($closeButton);
                frame.appendToFilterFrame($header, isTop);
            }
            if (this.forceSlideIn) {
                filterLayer.addClass('ss360-filter--slide');
            }
            else {
                filterLayer.removeClass('ss360-filter--slide');
            }
        }
    }
    createKeyToNameMapping(filterIdToName) {
        this.keyToName = this.allFilters.reduce((acc, filterGroup) => {
            acc[filterGroup.key] = filterGroup.name;
            return acc;
        }, {});
        if (this.context.pluginConfiguration.results.nameParsing && filterIdToName !== undefined) {
            Object.keys(filterIdToName).forEach((filterId) => {
                if (this.keyToName[filterId] === undefined) {
                    this.keyToName[filterId] = filterIdToName[filterId];
                }
            });
        }
    }
    changeHandler(id, val) {
        const groupCache = this.groupCache;
        const key = id;
        this.activeFilter[key] = val;
        groupCache.lastInteractedGroupKey = key;
        const $node = sxQuery(`#ss360-filter-${StringHelper.getSafeKey(groupCache.lastInteractedGroupKey)} .ss360-filter__button`);
        const isSlideIn = this.isSlideIn();
        const updateOffsetTop = () => {
            const offsetTop = isSlideIn ? this.filterLayer.offset().top : sxQuery(window).scrollTop();
            groupCache.lastInteractedGroupOffsetTop = $node.offset().top - offsetTop;
        };
        updateOffsetTop();
        let isUpdating = false;
        sxQuery(isSlideIn ? this.filterLayer : window).off('scroll.filterJump').on('scroll.filterJump', () => {
            if (isUpdating)
                return;
            isUpdating = true;
            setTimeout(() => {
                updateOffsetTop();
                isUpdating = false;
            }, 5);
        });
        let allowFalse = false;
        if (val === false) {
            const filterGroup = this.filterGroups.filter(group => group.key === id)[0];
            if (filterGroup !== undefined) {
                allowFalse = (filterGroup.values || []).length > 1;
            }
        }
        // @ts-ignore
        if (val === undefined || (val === false && !allowFalse) || (val !== true && val.length === 0 && !('min' in val))) {
            delete this.activeFilter[key];
        }
        if (this.context.hasInsights()) {
            const data = `${key}<#>${this.keyToName[key]}=>${this.activeFilter[key] !== undefined ? JSON.stringify(this.activeFilter[key]) : '--'}`;
            this.context.getInsights().trackFilterInteraction(this.query, data);
        }
        if (this.onChange === undefined || typeof this.onChange !== 'function') {
            return;
        }
        Object.keys(this.activeFilter).forEach((otherKey) => {
            if (this.filterModificationMap[otherKey] !== undefined) {
                this.activeFilter[otherKey] = this.activeFilter[otherKey].map((otherVal) => this.filterModificationMap[otherKey][otherVal] || otherVal);
            }
        });
        this.onChange(this.activeFilter, this.activeGuidedQuestions);
        sxQuery(window).off(`resize.${this.context.generateId('ss360FilterResize')}`);
    }
    getSetting(filterGroup) {
        return this.filterConfig.settings[filterGroup.name] || this.filterConfig.settings[filterGroup.key] || {};
    }
    expandHandler() {
        Object.values(this.groupCache.viewGroupByKey).forEach((filter) => filter.mount());
    }
    renderRangeFilter(filterGroup, settings, viewLabel, viewGroupKey) {
        const groupCache = this.groupCache;
        let filterAdded = false;
        const activeFilter = this.activeFilter;
        const key = filterGroup.key;
        if (filterGroup.min !== filterGroup.max || activeFilter[key] !== undefined) {
            const isDate = filterGroup.filterType === 'DATE';
            const step = isDate && settings.step === undefined ? 24 * 60 * 60 : settings.step; // one day by default
            const group = this.builder.range(viewGroupKey, key, viewLabel, filterGroup.min, filterGroup.max, step, settings.unit || filterGroup.unit, 
            // @ts-ignore
            activeFilter[key], settings, filterGroup.counts, isDate, filterGroup.precision, filterGroup.fractionalDigits, filterGroup.count);
            this.filters.push(group);
            groupCache.viewGroupByKey[viewGroupKey] = group;
            filterAdded = true;
            this.hasOptions = true;
        }
        return filterAdded;
    }
    renderTreeFilter(filterGroup, viewLabel, viewGroupKey) {
        const groupCache = this.groupCache;
        const values = filterGroup.categories || [];
        let filterAdded = false;
        const key = filterGroup.key;
        const activeFilter = this.activeFilter;
        this.hasOptions = this.hasOptions || values.length > 0;
        // @ts-ignore
        const group = this.builder.categoryTree(viewGroupKey, key, viewLabel, values, activeFilter[key]);
        this.filters.push(group);
        groupCache.viewGroupByKey[viewGroupKey] = group;
        filterAdded = true;
        return filterAdded;
    }
    parseCategoryTreeValues(key, values) {
        const filterDeletionValueMap = this.filterDeletionValueMap;
        filterDeletionValueMap[key] = {};
        if (!this.context.pluginConfiguration.results.nameParsing) {
            return values;
        }
        const activeFilter = this.activeFilter;
        // @ts-ignore
        if ((activeFilter[key] || []).length > 0) {
            const flatValueMap = {};
            const populateFlatValueMap = (arr) => arr.forEach((entry) => {
                flatValueMap[entry.value] = { name: entry.viewName || entry.name, value: entry.value || entry.key };
                if (entry.children.length !== undefined) {
                    populateFlatValueMap(entry.children);
                }
            });
            populateFlatValueMap(values);
            // @ts-ignore
            activeFilter[key] = activeFilter[key].map((val) => flatValueMap[val] ? flatValueMap[val].value : val);
            (this.activeOptions || []).forEach((obj, idx) => {
                const objKey = Object.keys(obj)[0];
                if (objKey === key) {
                    const newObj = {};
                    // @ts-ignore
                    newObj[objKey] = obj[objKey].map((option) => {
                        const valueEntry = flatValueMap[option];
                        const name = valueEntry ? valueEntry.name : option;
                        filterDeletionValueMap[key][name] = valueEntry ? valueEntry.value : option;
                        return name;
                    });
                    this.activeOptions[idx] = newObj;
                }
            });
        }
        const replaceKeys = (arr) => arr.map((entry) => {
            entry.conceptId = entry.name;
            if (entry.children !== undefined) {
                entry.children = replaceKeys(entry.children);
            }
            return entry;
        });
        return replaceKeys(values);
    }
    /**
     * Unify filter group value format
     *
     * @param filterGroup FilterGroup
     */
    transformMultiselectValues(filterGroup) {
        const activeFilter = this.activeFilter;
        const key = filterGroup.key;
        // @ts-ignore
        filterGroup.values = filterGroup.values.filter((value) => value !== null && value !== undefined);
        if (filterGroup.values.length > 0 && (typeof filterGroup.values[0] === 'string')) {
            // @ts-ignore
            filterGroup.values = filterGroup.values.map((value) => ({ key: value, name: value }));
        }
        else {
            this.isSimpleValueFormat = false;
            if (this.context.pluginConfiguration.results.nameParsing) {
                // @ts-ignore
                const keyValueMap = filterGroup.values.reduce((acc, value) => {
                    if (value.value !== undefined) {
                        acc[value.value] = value.name;
                    }
                    else if (value.key !== undefined) {
                        acc[value.key] = value.name;
                    }
                    else {
                        acc[value.name] = value.name;
                    }
                    return acc;
                }, {});
                // @ts-ignore
                filterGroup.values = filterGroup.values.map((value) => (Object.assign(Object.assign({}, value), { key: value.name, name: value.name })));
                if (activeFilter[key] !== undefined) {
                    // @ts-ignore
                    activeFilter[key] = activeFilter[key] === true ? true : activeFilter[key].map((o) => keyValueMap[o]);
                }
                if (filterGroup.counts !== undefined) {
                    filterGroup.counts = Object.keys(filterGroup.counts).reduce((acc, countKey) => {
                        const countName = keyValueMap[countKey];
                        acc[countName] = filterGroup.counts[countKey];
                        return acc;
                    }, {});
                }
                (this.activeOptions || []).forEach((obj, idx) => {
                    const filterModificationMap = this.filterModificationMap;
                    const objKey = Object.keys(obj)[0];
                    if (objKey === key) {
                        const newObj = {};
                        if (filterModificationMap[objKey] === undefined) {
                            filterModificationMap[objKey] = {};
                        }
                        if (obj[objKey] === true) {
                            newObj[objKey] = true;
                        }
                        else {
                            // @ts-ignore
                            newObj[objKey] = obj[objKey].map((option) => {
                                // @ts-ignore
                                if (keyValueMap[option] !== undefined && keyValueMap[option] !== option) {
                                    // @ts-ignore
                                    filterModificationMap[objKey][keyValueMap[option]] = option;
                                }
                                // @ts-ignore
                                return keyValueMap[option];
                            });
                        }
                        this.activeOptions[idx] = newObj;
                    }
                });
            }
        }
        // @ts-ignore
        filterGroup.values = filterGroup.values.filter((value) => value.key !== undefined && value.key !== null && value.name !== null && value.name !== undefined);
    }
    renderMultiselectFilter(filterGroup, filterSettings, viewLabel, viewGroupKey) {
        const groupCache = this.groupCache;
        if (filterGroup.values.length !== 0) {
            const { showOnSingleResult } = this.context.pluginConfiguration.filters;
            const activeFilter = this.activeFilter;
            const key = filterGroup.key;
            if (filterGroup.type === FilterType.NumericValues) {
                filterGroup.values = filterGroup.values.map((value) => (Object.assign(Object.assign({}, value), { key: value.key || `${value.value}` }))); // @ts-ignore
            }
            // @ts-ignore
            if ((showOnSingleResult || filterGroup.type === FilterType.Boolean)
                || !(filterGroup.values.length === 1 && (filterGroup.counts || {})[filterGroup.values[0].key] === this.totalResults && activeFilter[filterGroup.key] === undefined)) { // don't show this group, if there is only one option which is available for all results
                this.hasOptions = true;
                // @ts-ignore
                const group = this.builder.multipleChoice(viewGroupKey, key, viewLabel, filterGroup.values, activeFilter[key], filterGroup.counts || {}, filterGroup.type === FilterType.Color, this.filterModificationMap[key], filterGroup.type === FilterType.Boolean, filterSettings.disableSearch === true);
                this.filters.push(group);
                groupCache.viewGroupByKey[viewGroupKey] = group;
                return true;
            }
        }
        return false;
    }
    renderFilters(clusteredFilters) {
        const groupCache = this.groupCache;
        let wasNewFilterAdded = false;
        const filterConfig = this.context.pluginConfiguration.filters;
        const transformedFilterValues = {};
        clusteredFilters.forEach((cluster) => {
            const filterGroups = cluster.filters;
            for (let i = 0; i < (filterConfig.limit || 9999) && i < filterGroups.length; i++) {
                try {
                    const filterGroup = filterGroups[i];
                    const singleFilterConfig = this.getSetting(filterGroup);
                    let viewLabel = filterGroup.name;
                    if (singleFilterConfig.label !== undefined) {
                        viewLabel = singleFilterConfig.label;
                    }
                    const key = filterGroup.key;
                    const viewGroupKey = cluster.label ? `${StringHelper.getSafeKey(cluster.label)}${FILTER_GROUP_SEPARATOR}${key}` : key;
                    let filterViewGroup = groupCache.viewGroupByKey[viewGroupKey];
                    if (filterViewGroup !== undefined && !filterViewGroup.getWrapper().isMounted()) {
                        filterViewGroup = undefined;
                    }
                    let filterAdded = false;
                    let result = false;
                    if ('min' in filterGroup) {
                        if (singleFilterConfig.unit === undefined && filterGroup.unit !== undefined) {
                            this.units[key] = filterGroup.unit;
                        }
                        result = filterViewGroup !== undefined ? filterViewGroup.update(filterGroup, this.activeFilter[key]) : this.renderRangeFilter(filterGroup, singleFilterConfig, viewLabel, viewGroupKey);
                        filterAdded = result;
                    }
                    else if (filterGroup.type === FilterType.CategoryTree || filterGroup.filterType === FilterType.CategoryTree || filterGroup.type === FilterType.Tree || filterGroup.filterType === FilterType.Tree) {
                        const values = filterGroup.categories || [];
                        result = filterViewGroup !== undefined ? filterViewGroup.update(filterGroup, this.activeFilter[key], values) : this.renderTreeFilter(filterGroup, viewLabel, viewGroupKey);
                        filterAdded = result;
                    }
                    else {
                        if (filterGroup.type !== FilterType.Boolean && transformedFilterValues[key] === undefined) {
                            this.transformMultiselectValues(filterGroup);
                            transformedFilterValues[key] = true;
                        }
                        result = filterViewGroup !== undefined ? filterViewGroup.update(filterGroup, this.activeFilter[key], this.filterModificationMap[key]) : this.renderMultiselectFilter(filterGroup, singleFilterConfig, viewLabel, viewGroupKey);
                        filterAdded = result;
                    }
                    if (filterAdded && filterViewGroup !== undefined) {
                        this.filters.push(filterViewGroup);
                    }
                    if (filterAdded && filterGroup.visible === false && this.filters[this.filters.length - 1]) {
                        this.filters[this.filters.length - 1].addClass('ss360-filter__group--hidden');
                    }
                    const isLast = i === (filterConfig.limit || 9999) - 1 || i === filterGroups.length - 1;
                    if (isLast && this.filters[this.filters.length - 1] !== undefined) {
                        this.filters[this.filters.length - 1].addClass('ss360-filter__group--last');
                    }
                    if (filterAdded && this.previousFilterGroups.indexOf(viewGroupKey) !== -1) {
                        this.previousFilterGroups.splice(this.previousFilterGroups.indexOf(viewGroupKey), 1);
                    }
                    else if (filterAdded) {
                        wasNewFilterAdded = true;
                    }
                }
                catch (ex) {
                    Logger.warn(ex);
                }
            }
        });
        return wasNewFilterAdded;
    }
    handleFilterGroups(clusters) {
        this.filterLayer.find('.ss360-filter__group-label').remove();
        const collapsedFilterGroups = this.context.readObject('collapsedFilterGroups', []);
        const hasDefaultGroup = clusters.some((c) => c.label === undefined);
        clusters.filter((c) => c.label !== undefined).forEach((c, idx) => {
            let isEnabled = collapsedFilterGroups.indexOf(c.label) === -1;
            const keys = Object.keys(this.groupCache.viewGroupByKey);
            const allGroups = keys.filter((k) => k.startsWith(`${StringHelper.getSafeKey(c.label)}_`));
            if (allGroups.length > 0) {
                const $label = sxQuery(`<button class="ss360-n-button ss360-filter__group-label ss360-filter__group-label--${StringHelper.getSafeKey(c.label)}"></button>`);
                if (idx === 0 && !hasDefaultGroup) {
                    $label.addClass('ss360-filter__group-label--first');
                }
                const $text = sxQuery('<span></span>');
                $text.text(c.label);
                $label.append($text);
                $label.append(Icons.getSvgIconNode(Icons.SIMPLE_ARROW, UiColors.GREY_TEXT, 'ss360-filter__group-icon', 24, 24));
                const onToggled = () => {
                    $label.attr('aria-label', (isEnabled ? 'Hide' : 'Show') + ` ${c.label} Filters`);
                    allGroups.forEach((groupKey) => {
                        const group = this.groupCache.viewGroupByKey[groupKey];
                        const wrapper = group.getWrapper();
                        if (isEnabled) {
                            wrapper.show();
                            group.mount();
                        }
                        else {
                            wrapper.hide();
                        }
                    });
                    if (!isEnabled) {
                        $label.addClass('ss360-filter__group-label--collapsed');
                    }
                    else {
                        $label.removeClass('ss360-filter__group-label--collapsed');
                    }
                };
                $label.on('click', () => {
                    isEnabled = !isEnabled;
                    if (isEnabled) {
                        collapsedFilterGroups.splice(collapsedFilterGroups.indexOf(c.label), 1);
                    }
                    else if (collapsedFilterGroups.indexOf(c.label) === -1) {
                        collapsedFilterGroups.push(c.label);
                    }
                    this.context.storeObject('collapsedFilterGroups', collapsedFilterGroups);
                    onToggled();
                });
                setTimeout(() => {
                    onToggled();
                }, 0);
                const firstGroupWrap = this.groupCache.viewGroupByKey[allGroups[0]].getWrapper().get(0);
                firstGroupWrap.parentNode.insertBefore($label.get(0), firstGroupWrap);
                allGroups.forEach((g) => {
                    this.groupCache.viewGroupByKey[g].addClass('ss360-filter__group--clustered');
                });
            }
        });
    }
    renderMobileToggle() {
        if (this.position !== FilterPosition.Left) {
            return;
        }
        const filterConfig = this.filterConfig;
        const filterLayer = this.filterLayer;
        const layoutFrame = this.context.layer.get();
        let showMobileButton = layoutFrame.getMobileFilterToggle();
        if (showMobileButton.length === 0) {
            const filterAriaLabel = this.toggleButtonLabel || filterConfig.toggleButtonLabel || 'Show Filter';
            showMobileButton = sxQuery(`<button type="button" class="ss360-filter__mobile-toggle ss360-n-button">
				<i class="ss360-filter__button-icon">
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="${UiColors.GREY_TEXT}" d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
				</i>
			</button>`);
            showMobileButton.attr('title', filterAriaLabel);
            showMobileButton.attr('aria-label', filterAriaLabel);
            if (filterConfig.toggleButtonLabel.length > 0) {
                showMobileButton.append(`<span>${this.toggleButtonLabel || filterConfig.toggleButtonLabel}</span>`);
                showMobileButton.addClass('ss360-filter__mobile-toggle--labeled');
            }
            showMobileButton.append('<span class="ss360-filter__mobile-toggle-count"></span>');
            layoutFrame.appendToFilterFrame(showMobileButton, true);
        }
        showMobileButton.find('.ss360-filter__mobile-toggle-count').text(`${this.filterGroups.length}`);
        if (this.forceSlideIn) {
            showMobileButton.addClass('ss360-filter__mobile-toggle--slide');
        }
        else {
            showMobileButton.removeClass('ss360-filter__mobile-toggle--slide');
        }
        let mobileBackdrop = layoutFrame.getFilterBackdrop();
        const showMobile = () => {
            filterLayer.css('display', 'block');
            this.expandHandler();
            sxQuery('body').addClass('ss360-u-hide-y-overflow');
            setTimeout(() => {
                filterLayer.addClass('ss360-filter--slide--open');
                mobileBackdrop.addClass('ss360-filter__backdrop--open');
            }, 0);
            this.context.layer.get().ensureSidebarVisibility();
            persistentFilterOptions.ss360AreMobileSuggestsOpen = true;
            this.context.emitEvent('open-filter-drawer');
        };
        if (persistentFilterOptions.ss360AreMobileSuggestsOpen === true) {
            filterLayer.css('transitionDuration', '0s');
            showMobile();
            setTimeout(() => {
                filterLayer.css('transitionDuration', null);
            }, 300);
        }
        showMobileButton.off('click.mobileToggle').on('click.mobileToggle', showMobile);
        mobileBackdrop.off('click.mobileToggle').on('click.mobileToggle', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.hideMobile();
        });
        filterLayer.find('.ss360-close-button--filter').off('click.mobileToggle').on('click.mobileToggle', (e) => {
            e.preventDefault();
            e.stopPropagation();
            setTimeout(() => {
                this.hideMobile();
            }, 0);
        });
        const skipFilterButton = sxQuery(`<button type="button" class="ss360-skip-link ss360-n-button">${this.filterConfig.skipFilter}</button>`);
        skipFilterButton.on('click', () => {
            layoutFrame.focusFirstResult();
        });
        filterLayer.find('.ss360-skip-link:not(.ss360-skip-link--group)').remove();
        filterLayer.prepend(skipFilterButton);
    }
    hideMobile() {
        let mobileBackdrop = this.context.layer.get().getFilterBackdrop();
        const filterLayer = this.filterLayer;
        const isActive = filterLayer.hasClass('ss360-filter--slide--open');
        filterLayer.removeClass('ss360-filter--slide--open');
        mobileBackdrop.removeClass('ss360-filter__backdrop--open');
        if (isActive) {
            sxQuery('body').removeClass('ss360-u-hide-y-overflow');
        }
        persistentFilterOptions.ss360AreMobileSuggestsOpen = false;
        setTimeout(() => {
            this.filterLayer.css('display', null);
        }, 300);
        if (isActive) {
            this.context.emitEvent('close-filter-drawer');
        }
    }
    ;
    mount() {
        const filters = this.filters;
        const filterLayer = this.filterLayer;
        filterLayer.addClass('ss360-filter--mounting');
        const $layer = sxQuery(`#${this.filterLayerId}`);
        if (filters.length > 100) { // hide so it doesn't retrigger so many repaints
            $layer.hide();
        }
        const isTop = this.position === FilterPosition.Top;
        filters.forEach((filter) => {
            const filterWrap = filter.getWrapper();
            this.context.layer.get().appendToFilterFrame(filterWrap, isTop);
        });
        if (filters.length > 100) {
            $layer.show();
        }
        filters.forEach((filter) => filter.mount());
        setTimeout(() => {
            filterLayer.removeClass('ss360-filter--mounting');
        }, 300);
    }
    renderDeleteBar() {
        const filterConfig = this.filterConfig;
        const layoutFrame = this.context.layer.get();
        const attachToFilterLayer = this.position === FilterPosition.Left && filterConfig.mobileQuickDeleteBarInLayer && SxQueryUtils.matchesMediaQuery(MediaQueryMatchType.Max, 991);
        const show = filterConfig.showQuickDelete && (this.activeOptions.length > 0 || this.activeGuidedQuestions.length > 0);
        let mobileDeleteBar = new SxQueryObject([]);
        layoutFrame.detachFilterDeleteBar();
        if (attachToFilterLayer && show) {
            if (this.filterLayer.find('.ss360-filter__delete-bar').length === 0) {
                mobileDeleteBar = sxQuery('<section role="search" class="ss360-n-section ss360-filter__delete-bar"></section>');
                mobileDeleteBar.attr('aria-label', filterConfig.selectedFilterOptionsLabel);
                const $header = this.filterLayer.find('.ss360-filter__header');
                if ($header.length > 0) {
                    const headerNode = $header.get(0);
                    if (headerNode.nextElementSibling !== null) {
                        headerNode.parentNode.insertBefore(mobileDeleteBar.get(0), headerNode.nextElementSibling);
                    }
                    else {
                        this.filterLayer.append(mobileDeleteBar);
                    }
                }
                else {
                    this.filterLayer.prepend(mobileDeleteBar);
                }
            }
            else {
                mobileDeleteBar = this.filterLayer.find('.ss360-filter__delete-bar');
                mobileDeleteBar.empty();
            }
        }
        if (show) {
            const deleteAllButton = sxQuery(`<button type="button" aria-label="${filterConfig.deleteAllLabel}" class="ss360-filter__delete-button 
			ss360-filter__delete-button--all ss360-flex ss360-flex--align-center ss360-n-button">${filterConfig.deleteAllLabel}</button>`);
            deleteAllButton.on('click', () => {
                this.activeGuidedQuestions = [];
                sxQuery(deleteAllButton.parents('.ss360-filter__delete-bar')).fadeOut(undefined, () => {
                    layoutFrame.detachFilterDeleteBar();
                    if (attachToFilterLayer) {
                        mobileDeleteBar.remove();
                    }
                });
                Object.keys(this.activeFilter).forEach((key) => {
                    delete this.activeFilter[key];
                });
                this.changeHandler('0', undefined); // delete all filter
            });
            const themeColor = this.context.pluginConfiguration.style.themeColor;
            const createButton = (label, keyLabel, val) => sxQuery(`<button type="button" aria-label="${label}" class="ss360-flex ss360-flex--align-center ss360-filter__delete-button ss360-n-button">
			${keyLabel ? `<span class="ss360-filter__delete-button-key">${keyLabel}: </span>` : ''}
			<span class="ss360-filter__delete-button-value">${val}</span>
			<i class="ss360-filter__delete-button-icon">${Icons.getSvgIcon(Icons.CROSS, themeColor, 'ss360-filter__delete-button-svg')}</i>
			<i class="ss360-filter__delete-button-icon ss360-filter__delete-button-icon--active">${Icons.getSvgIcon(Icons.FILLED_CROSS, themeColor, 'ss360-filter__delete-button-svg')}</i>
			</button>`);
            const setFilterMap = {};
            this.activeOptions.forEach((activeOption) => {
                const afkey = Object.keys(activeOption)[0];
                let afname = this.keyToName[afkey] || this.fallbackNames[afkey];
                if (afname === undefined) { // the view name is not available (probably unselecting the option wouldn't influence the result set) --> ignore
                    return;
                }
                const afGroup = this.allFilters.filter((group) => group.key === afkey)[0];
                const configuredLabel = afGroup !== undefined ? this.getSetting(afGroup).label : undefined;
                if (configuredLabel !== undefined) {
                    afname = configuredLabel;
                }
                const createDeleteButton = (label, displayValue, dataValue, keyLabel) => {
                    const val = StringHelper.escapeHtml(`${displayValue[0].toUpperCase()}${displayValue.substring(1)}`);
                    const btn = createButton(StringHelper.escapeHtml(label), StringHelper.escapeHtml(keyLabel || ''), val);
                    if (dataValue === '~false') {
                        btn.find('.ss360-filter__delete-button-value').addClass('ss360-filter__delete-button-value--negative');
                    }
                    btn.on('click', () => {
                        btn.fadeOut();
                        if (dataValue === undefined || dataValue === '~true' || dataValue === '~false') {
                            if (Object.keys(this.activeFilter).length === 1) {
                                deleteAllButton.fadeOut();
                            }
                            this.changeHandler(afkey || afname, undefined); // remove range filter
                        }
                        else {
                            // @ts-ignore
                            const vcopy = [...this.activeFilter[afkey]];
                            if (Object.keys(this.activeFilter).length === 1 && vcopy.length === 1) {
                                deleteAllButton.fadeOut();
                            }
                            if (this.filterDeletionValueMap[afkey] !== undefined && this.filterDeletionValueMap[afkey][dataValue] !== undefined) {
                                dataValue = this.filterDeletionValueMap[afkey][dataValue];
                            }
                            if (vcopy.indexOf(dataValue) !== -1) {
                                vcopy.splice(vcopy.indexOf(dataValue), 1);
                            }
                            this.changeHandler(afkey || afname, vcopy); // remove multiselect filter
                        }
                        if ((!attachToFilterLayer && layoutFrame.getDeleteBarChildCount(false) === 0) || (attachToFilterLayer && mobileDeleteBar.find('.ss360-filter__delete-button:not(.ss360-filter__delete-button--all)').length === 0)) {
                            layoutFrame.detachFilterDeleteBar();
                            if (attachToFilterLayer) {
                                mobileDeleteBar.remove();
                            }
                        }
                    });
                    return btn;
                };
                const activeOptionVal = activeOption[afkey];
                // @ts-ignore
                if (activeOptionVal.min !== undefined && !Helper.isNaN(parseFloat(activeOptionVal.min)) &&
                    // @ts-ignore
                    activeOptionVal.max !== undefined && !Helper.isNaN(parseFloat(activeOptionVal.max))) {
                    const tempObj = {
                        name: afname,
                        key: afkey
                    };
                    let unit = this.getSetting(tempObj).unit;
                    if (unit === undefined) {
                        unit = this.units[afkey];
                    }
                    if (unit === undefined) {
                        // @ts-ignore
                        unit = activeOptionVal.unit;
                    }
                    const afunit = unit !== undefined ? `${unit}` : '';
                    // @ts-ignore
                    const minVal = activeOptionVal.min;
                    // @ts-ignore
                    const maxVal = activeOptionVal.max;
                    let afmin = minVal.toFixed(minVal === Math.round(minVal) ? 0 : 2);
                    let afmax = maxVal.toFixed(maxVal === Math.round(maxVal) ? 0 : 2);
                    if (afGroup !== undefined && afGroup.filterType === 'DATE') {
                        const factor = afGroup.precision === DatePrecision.MS ? 1 : 1000;
                        afmin = new Date(minVal * factor).toLocaleDateString(this.filterConfig.dateFormatLocale);
                        afmax = new Date(maxVal * factor).toLocaleDateString(this.filterConfig.dateFormatLocale);
                    }
                    else {
                        afmin = Helper.addUnitToNumber(afmin, afunit);
                        afmax = Helper.addUnitToNumber(afmax, afunit);
                    }
                    // @ts-ignore
                    const $deleteButton = createDeleteButton(`Clear ${afname} filter`, `${afmin} - ${afmax}`, undefined, activeOptionVal.shortName);
                    if (attachToFilterLayer) {
                        mobileDeleteBar.append($deleteButton);
                    }
                    else {
                        layoutFrame.appendToFilterDeleteBar($deleteButton, this.deleteFilterBottom);
                    }
                }
                else if (activeOptionVal instanceof Array) {
                    // @ts-ignore
                    activeOptionVal.forEach((afval) => {
                        let displayName = afval;
                        const isTree = afGroup !== undefined && (afGroup.type === FilterType.CategoryTree || afGroup.type === FilterType.Tree);
                        if (afGroup !== undefined && (!this.isSimpleValueFormat || isTree)) {
                            if (isTree) {
                                const stack = JSON.parse(JSON.stringify(afGroup.categories));
                                while (stack.length > 0) {
                                    const item = stack.splice(0, 1)[0];
                                    if (item.conceptId !== undefined && item.conceptId.toString() === afval.toString()) {
                                        displayName = item.name;
                                        break;
                                    }
                                    (item.children || []).forEach((child) => {
                                        stack.push(child);
                                    });
                                }
                            }
                            else {
                                // @ts-ignore
                                displayName = (afGroup.values.filter((val) => val.key === afval)[0] || {}).name || afval;
                            }
                        }
                        if (afGroup === undefined && this.optionViewNames[`${afkey}<#>${displayName}`] !== undefined) {
                            displayName = this.optionViewNames[`${afkey}<#>${displayName}`];
                        }
                        const duplicateKey = `${afkey}<#>${displayName}`;
                        if (setFilterMap[duplicateKey]) {
                            return;
                        }
                        if (displayName !== undefined) {
                            setFilterMap[duplicateKey] = true;
                            const $btn = createDeleteButton(`Clear ${displayName} filter option of ${afname}`, displayName, afval);
                            if (attachToFilterLayer) {
                                mobileDeleteBar.append($btn);
                            }
                            else {
                                layoutFrame.appendToFilterDeleteBar($btn, this.deleteFilterBottom);
                            }
                        }
                    });
                }
                else if (activeOptionVal === true || activeOptionVal === false) {
                    const displayName = afGroup !== undefined ? afGroup.name : undefined;
                    if (displayName !== undefined) {
                        const $btn = createDeleteButton(`Clear ${displayName} filter option`, displayName, `~${activeOptionVal}`);
                        if (attachToFilterLayer) {
                            mobileDeleteBar.append($btn);
                        }
                        else {
                            layoutFrame.appendToFilterDeleteBar($btn, this.deleteFilterBottom);
                        }
                    }
                }
            });
            this.activeGuidedQuestions.forEach((question) => {
                if (question.name && (question.name !== 'skip' || question.answerId !== undefined)) {
                    const btn = createButton('', '', question.name);
                    btn.addClass('ss360-filter__delete-button--guided-question');
                    if (attachToFilterLayer) {
                        mobileDeleteBar.append(btn);
                    }
                    else {
                        layoutFrame.appendToFilterDeleteBar(btn, this.deleteFilterBottom);
                    }
                    btn.on('click', () => {
                        btn.fadeOut();
                        if (layoutFrame.getDeleteBarChildCount() === 2) {
                            deleteAllButton.fadeOut();
                        }
                        btn.fadeOut();
                        Object.keys(this.activeFilter).forEach((otherKey) => {
                            if (this.filterModificationMap[otherKey] !== undefined) {
                                this.activeFilter[otherKey] = this.activeFilter[otherKey].map((otherVal) => this.filterModificationMap[otherKey][otherVal] || otherVal);
                            }
                        });
                        this.onChange(this.activeFilter, this.activeGuidedQuestions.filter((q) => q.questionId !== question.questionId));
                    });
                }
            });
            if (attachToFilterLayer) {
                mobileDeleteBar.append(deleteAllButton);
            }
            else {
                layoutFrame.appendToFilterDeleteBar(deleteAllButton, this.deleteFilterBottom);
            }
            if ((!attachToFilterLayer && layoutFrame.getDeleteBarChildCount(false) === 0) || (attachToFilterLayer && mobileDeleteBar.find('.ss360-filter__delete-button:not(.ss360-filter__delete-button--all)').length === 0)) {
                layoutFrame.detachFilterDeleteBar();
                if (attachToFilterLayer) {
                    mobileDeleteBar.remove();
                }
            }
        }
    }
    bindMappings(filterMapping) {
        const groupCache = this.groupCache;
        const pluginConfig = this.context.pluginConfiguration;
        const layoutFrame = this.context.layer.get();
        const navigationConfiguration = pluginConfig.layout.navigation;
        const navigationPosition = navigationConfiguration.position;
        const navigationType = navigationConfiguration.type;
        const collapseSidebar = this.position === FilterPosition.Left && navigationPosition !== NavigationPosition.Left && !this.forceSlideIn;
        if (filterMapping !== undefined && Object.keys(filterMapping).length > 0 && navigationType === NavigationType.Tabs && navigationPosition !== NavigationPosition.None && layoutFrame.getResultGroups().length > 1) {
            const toggleFilterVisibility = (mapping = []) => {
                const groupsByKey = groupCache.groupByKey || {};
                let visibleGroupCount = 0;
                Object.keys(groupsByKey).forEach((key, idx) => {
                    const group = groupsByKey[key];
                    if (key.indexOf(FILTER_GROUP_SEPARATOR) !== -1) { // handle filter groups
                        key = key.split(FILTER_GROUP_SEPARATOR)[1];
                    }
                    const wrapper = group.wrapper;
                    if (wrapper !== undefined) {
                        if (mapping.indexOf(key) !== -1) {
                            wrapper.show();
                            visibleGroupCount++;
                            if (this.filters[idx] !== undefined && this.position !== FilterPosition.Top) {
                                if (visibleGroupCount <= this.filterConfig.expandedGroupsCount) {
                                    this.filters[idx].show();
                                }
                            }
                        }
                        else {
                            wrapper.hide();
                        }
                    }
                });
                const layoutFrame = this.context.layer.get();
                if (visibleGroupCount === 0) {
                    layoutFrame.getMobileFilterToggle().hide();
                    this.filterLayer.hide();
                    if (collapseSidebar) {
                        layoutFrame.collapseSidebar();
                    }
                }
                else {
                    layoutFrame.getMobileFilterToggle().show();
                    this.filterLayer.show();
                    if (collapseSidebar) {
                        layoutFrame.uncollapseSidebar();
                    }
                    (groupCache.filters || []).forEach((f) => f.mount()); // e.g. to rerender the sliders
                }
            };
            this.context.subscribe('navigationChange', `filterMappingHandler-${this.position}`, (openTab) => {
                toggleFilterVisibility(openTab === pluginConfig.layout.navigation.allResultsTabName || openTab === '*' ? Object.keys(groupCache.groupByKey || {}) : filterMapping[openTab]);
            });
            const openTab = layoutFrame.getActiveNavItem().data('cgkey');
            if (openTab === 'ss360_all_results') {
                toggleFilterVisibility(Object.keys(groupCache.groupByKey || {}));
            }
            else {
                const keys = Object.keys(filterMapping);
                let matchingFilters;
                for (let i = 0; i < keys.length; i++) {
                    const currentKey = keys[i];
                    if (openTab === StringHelper.getSafeKey(currentKey)) {
                        matchingFilters = filterMapping[currentKey];
                        break;
                    }
                }
                toggleFilterVisibility(matchingFilters);
            }
        }
    }
}
export default Filter;
